import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import imageCompression from 'browser-image-compression';
import './ConfigureUserDisplay.css';
import { getAuthHeader } from '../utils/tokenManager';

const apiUrl = process.env.REACT_APP_DJANGO_API_URL;

const ConfigureUserDisplay = ({ onUserDisplayChange, userDisplay, languageSetting }) => {
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadStatus, setUploadStatus] = useState('');
  const [isCompressing, setIsCompressing] = useState(false);
  const [imageExists, setImageExists] = useState(false);
  const { uuid } = useParams();
  const [searchParams] = useSearchParams();
  const userId = searchParams.get('user_id') || '1';
  const cdnImageUrl = `https://cdn.eztiptap.com/user_photos/${uuid}/user_${userId}.png`;

  // Define text content based on language setting
  const textContent = {
    ENGLISH: {
      title: "Configure Your Profile",
      displayNameLabel: "Display Name",
      displayNamePlaceholder: "Enter your display name",
      displayNameRequired: "Display name is required",
      profileImageLabel: "Profile Image",
      changeImage: "Change Image",
      uploadImage: "Upload Image",
      useImage: "Use Image",
      compressing: "Compressing...",
      uploading: "Uploading...",
      uploadSuccessful: "Upload successful!",
      uploadFailed: "Upload failed: ",
      selectImageFirst: "Please select an image first",
      errorPreparingImage: "Error preparing image: "
    },
    SPANISH: {
      title: "Configurar Su Perfil",
      displayNameLabel: "Nombre De Perfil",
      displayNamePlaceholder: "Ingrese su nombre de perfil",
      displayNameRequired: "El nombre de perfil es obligatorio",
      profileImageLabel: "Imagen De Perfil",
      changeImage: "Cambiar Imagen",
      uploadImage: "Subir Imagen",
      useImage: "Usar Imagen",
      compressing: "Comprimiendo...",
      uploading: "Subiendo...",
      uploadSuccessful: "¡Subida exitosa!",
      uploadFailed: "Error al subir: ",
      selectImageFirst: "Por favor seleccione una imagen primero",
      errorPreparingImage: "Error al preparar la imagen: "
    }
  };

  // Get the current language text
  const currentText = textContent[languageSetting] || textContent.ENGLISH;

  // Check if the profile image exists in the CDN
  useEffect(() => {
    const checkImage = async () => {
      try {
        const response = await fetch(cdnImageUrl);
        if (response.ok) {
          setImageExists(true);
          setImagePreview(cdnImageUrl);
        } else {
          setImageExists(false);
        }
      } catch (error) {
        console.error("Error checking profile image:", error);
        setImageExists(false);
      }
    };
    
    checkImage();
  }, [cdnImageUrl]);

  const handleDisplayNameChange = (e) => {
    const name = e.target.value;
    onUserDisplayChange(name);
  };

  const compressImage = async (file) => {
    const options = {
      maxSizeMB: 1,
      maxWidthOrHeight: 1024,
      useWebWorker: true,
    };

    try {
      setIsCompressing(true);
      const compressedFile = await imageCompression(file, options);
      console.log('Original file size:', file.size / 1024 / 1024, 'MB');
      console.log('Compressed file size:', compressedFile.size / 1024 / 1024, 'MB');
      return compressedFile;
    } catch (error) {
      console.error('Error compressing image:', error);
      throw error;
    } finally {
      setIsCompressing(false);
    }
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        const compressedFile = await compressImage(file);
        const reader = new FileReader();
        reader.onloadend = () => {
          setImagePreview(reader.result);
          setImageFile(compressedFile);
        };
        reader.readAsDataURL(compressedFile);
      } catch (error) {
        setUploadStatus(currentText.errorPreparingImage + error.message);
      }
    }
  };

  const handleImageUpload = async () => {
    if (!imageFile) {
      setUploadStatus(currentText.selectImageFirst);
      return;
    }

    try {
      setUploadStatus(currentText.uploading);

      const formData = new FormData();
      formData.append('image', imageFile);

      const response = await fetch(`${apiUrl}/api/${uuid}/upload_user_photo/?user_id=${userId}`, {
        method: 'POST',
        headers: getAuthHeader(),
        body: formData
      });

      let data;
      const textResponse = await response.text();
      try {
        data = JSON.parse(textResponse);
      } catch (e) {
        console.error('Server response:', textResponse);
        throw new Error('Invalid response from server');
      }
      
      if (!response.ok) {
        console.error('Server error details:', data);
        throw new Error(data.error || `Upload failed: ${response.statusText}`);
      }

      setUploadStatus(currentText.uploadSuccessful);
      // After successful upload, update the image preview to use the CDN URL
      setImageExists(true);
      setImagePreview(cdnImageUrl);

    } catch (error) {
      console.error('Full error details:', error);
      setUploadStatus(currentText.uploadFailed + error.message);
    }
  };

  return (
    <div className="display-config-container">
      <div className="display-config-content">
        <h2>{currentText.title}</h2>
        
        <div className="input-group">
          <label htmlFor="displayName">{currentText.displayNameLabel} <span style={{ color: '#dc2626' }}>*</span></label>
          <input
            type="text"
            id="displayName"
            className="display-input"
            value={userDisplay || ''}
            onChange={handleDisplayNameChange}
            maxLength={20}
            placeholder={currentText.displayNamePlaceholder}
            required
          />
          {!userDisplay.trim() && (
            <div className="error-text">{currentText.displayNameRequired}</div>
          )}
        </div>

        <div className="input-group">
          <label htmlFor="imageUpload">{currentText.profileImageLabel}</label>
          <div className="image-upload-container">
            {imagePreview && (
              <div className="image-preview">
                <img src={imagePreview} alt="Profile" />
              </div>
            )}
            <input
              type="file"
              id="imageUpload"
              className="image-input"
              accept="image/*"
              onChange={handleImageChange}
              disabled={isCompressing}
            />
            <div className="button-group">
              <label htmlFor="imageUpload" className="upload-button">
                {imagePreview ? currentText.changeImage : currentText.uploadImage}
              </label>
              {imageFile && (
                <button 
                  className="use-image-button" 
                  onClick={handleImageUpload}
                  disabled={isCompressing}
                >
                  {isCompressing ? currentText.compressing : currentText.useImage}
                </button>
              )}
            </div>
            {uploadStatus && (
              <div className={`upload-status ${uploadStatus.includes('failed') ? 'error' : 'success'}`}>
                {uploadStatus}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfigureUserDisplay; 